import { $t } from '@dabble/app';
import { Doc } from '@dabble/data/types';
import Tooltip from '@dabble/toolkit/TooltipComponent.svelte';
import { Editor, ModuleInitializer } from 'typewriter-editor';

const TRACK_READONLY_CLOSEST = 'format-track-ins, format-track-d3l, format-track-format-change';

let trackReadonlyTooltip: Tooltip | undefined;
let trackReadonlyTooltipTimeout: ReturnType<typeof setTimeout> | undefined;

function elementFromEventTarget(target: EventTarget | null): Element | null {
  if (!target || !(target instanceof Node)) return null;
  return target instanceof Element ? target : target.parentElement;
}

function removeTrackReadonlyTooltip() {
  if (trackReadonlyTooltipTimeout !== undefined) {
    clearTimeout(trackReadonlyTooltipTimeout);
    trackReadonlyTooltipTimeout = undefined;
  }
  if (trackReadonlyTooltip) {
    trackReadonlyTooltip.$destroy();
    trackReadonlyTooltip = undefined;
  }
}

/**
 * Readonly track spans are enforced in the DOM via `contentEditable: false` on the format elements
 * ([`track-changes-formats.ts`](./track-changes-formats.ts)). This module only adds hover tooltips.
 */
export function trackChangesModule(_doc: Doc, field: string): ModuleInitializer | null {
  if (field !== 'body') return null;

  return (editor: Editor) => {
    function onTrackFormatMouseOver(event: MouseEvent) {
      const el = elementFromEventTarget(event.target)?.closest(TRACK_READONLY_CLOSEST);
      if (!el) return;
      if (trackReadonlyTooltipTimeout !== undefined) {
        clearTimeout(trackReadonlyTooltipTimeout);
        trackReadonlyTooltipTimeout = undefined;
      }
      const html = $t('track_changes_readonly_tooltip');
      if (!trackReadonlyTooltip) {
        trackReadonlyTooltip = new Tooltip({
          target: document.body,
          props: {
            target: el,
            html,
            placement: 'top',
          },
        });
        (trackReadonlyTooltip as { removeTooltip?: () => void }).removeTooltip = removeTrackReadonlyTooltip;
      } else {
        trackReadonlyTooltip.$set({
          target: el,
          html,
          placement: 'top',
        });
      }
    }

    function onTrackFormatMouseOut(event: MouseEvent) {
      const rel = elementFromEventTarget(event.relatedTarget);
      if (!rel?.closest(TRACK_READONLY_CLOSEST)) {
        trackReadonlyTooltipTimeout = setTimeout(removeTrackReadonlyTooltip, 10);
      }
    }

    return {
      init() {
        editor.root.addEventListener('mouseover', onTrackFormatMouseOver);
        editor.root.addEventListener('mouseout', onTrackFormatMouseOut);
      },
      destroy() {
        removeTrackReadonlyTooltip();
        editor.root.removeEventListener('mouseover', onTrackFormatMouseOver);
        editor.root.removeEventListener('mouseout', onTrackFormatMouseOut);
      },
    };
  };
}
